import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Navigation } from './components/Navigation';
import { ErrorBoundary } from './components/ErrorBoundary';
// Auth Pages
import { LoginPage } from './pages/LoginPage';
import { RegisterStep1 } from './pages/RegisterStep1';
import { RegisterStep2 } from './pages/RegisterStep2';
import { RegisterStep3 } from './pages/RegisterStep3';
import { RegisterStep4 } from './pages/RegisterStep4';
// Member Pages
import { Dashboard } from './pages/Dashboard';
import { ProfilePage } from './pages/ProfilePage';
import { PaymentPage } from './pages/PaymentPage';
import { JobListings } from './pages/JobListings';
import { JobDetail } from './pages/JobDetail';
import { BenefitsPage } from './pages/BenefitsPage';
import { BenefitDetailPage } from './pages/BenefitDetailPage';
import { BenefitClaimPage } from './pages/BenefitClaimPage';
import { BenefitHistoryPage } from './pages/BenefitHistoryPage';
import { ChapterPage } from './pages/ChapterPage';
import { EventsPage } from './pages/EventsPage';
import { EventDetail } from './pages/EventDetail';
import { AnnouncementsPage } from './pages/AnnouncementsPage';
import { MeetingDetail } from './pages/MeetingDetail';
import { KnowledgeCenter } from './pages/KnowledgeCenter';
import { SettingsPage } from './pages/SettingsPage';
import { EditProfilePage } from './pages/EditProfilePage';
import { CertificateDownloadPage } from './pages/CertificateDownloadPage';
// Admin Pages
import { SuperAdminDashboard } from './pages/admin/SuperAdminDashboard';
import { AdminMembers } from './pages/AdminMembers';
import { AdminAnalytics } from './pages/admin/AdminAnalytics';
import { AdminSettings } from './pages/admin/AdminSettings';
import { AdminApprovals } from './pages/admin/AdminApprovals';
import { AdminActivities } from './pages/admin/AdminActivities';
import { AdminLogs } from './pages/admin/AdminLogs';
import { AdminReports } from './pages/admin/AdminReports';
// Chapter Leader Pages
import { ChapterDashboard } from './pages/chapter/ChapterDashboard';
import { StudentChapterDashboard } from './pages/chapter/StudentChapterDashboard';
import { ChapterMembers } from './pages/chapter/ChapterMembers';
import { ChapterReports } from './pages/chapter/ChapterReports';
// Committee Pages
import { CommitteeDashboard } from './pages/committee/CommitteeDashboard';
import { CommitteePolicies } from './pages/committee/CommitteePolicies';
import { CommitteeReports } from './pages/committee/CommitteeReports';
import { CommitteeApprovals } from './pages/committee/CommitteeApprovals';
// Event Coordinator Pages
import { EventCoordinatorDashboard } from './pages/events/EventCoordinatorDashboard';
import { EventsRegistrations } from './pages/events/EventsRegistrations';
import { EventsCertificates } from './pages/events/EventsCertificates';
// Finance Pages
import { FinanceDashboard } from './pages/finance/FinanceDashboard';
import { FinanceInvoices } from './pages/finance/FinanceInvoices';
import { FinanceReports } from './pages/finance/FinanceReports';
import { EventsReports } from './pages/events/EventsReports';
import { JobApplicationPage } from './pages/JobApplicationPage';
import { EventRegistrationPage } from './pages/EventRegistrationPage';
import { PaymentGateway } from './pages/PaymentGateway';
import { ChapterDetailPage } from './pages/ChapterDetailPage';
import { JobManagementPage } from './pages/company/JobManagementPage';
import { MemberManagementPage } from './pages/organization/MemberManagementPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import { CreateEventPage } from './pages/CreateEventPage';
import { EventApprovalsPage } from './pages/committee/EventApprovalsPage';
import { CreateJobPage } from './pages/CreateJobPage';
import { UpgradeMembershipPage } from './pages/UpgradeMembershipPage';
import { DocumentViewerPage } from './pages/DocumentViewerPage';
function AppRoutes() {
  const {
    isAuthenticated,
    user
  } = useAuth();
  return <Routes data-id="element-0">
      {/* Public Routes */}
      <Route path="/login" element={isAuthenticated ? <Navigate to={getDefaultRoute(user?.role)} replace data-id="element-2" /> : <LoginPage data-id="element-3" />} data-id="element-1" />
      <Route path="/register/step1" element={<RegisterStep1 data-id="element-5" />} data-id="element-4" />
      <Route path="/register/step2" element={<RegisterStep2 data-id="element-7" />} data-id="element-6" />
      <Route path="/register/step3" element={<RegisterStep3 data-id="element-9" />} data-id="element-8" />
      <Route path="/register/step4" element={<RegisterStep4 data-id="element-11" />} data-id="element-10" />

      {/* Super Admin Routes */}
      <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={['super_admin']} data-id="element-13">
            <Navigation data-id="element-14" />
            <SuperAdminDashboard data-id="element-15" />
          </ProtectedRoute>} data-id="element-12" />
      <Route path="/admin/members" element={<ProtectedRoute allowedRoles={['super_admin']} data-id="element-17">
            <Navigation data-id="element-18" />
            <AdminMembers data-id="element-19" />
          </ProtectedRoute>} data-id="element-16" />
      <Route path="/admin/approvals" element={<ProtectedRoute allowedRoles={['super_admin']} data-id="element-21">
            <Navigation data-id="element-22" />
            <AdminApprovals data-id="element-23" />
          </ProtectedRoute>} data-id="element-20" />
      <Route path="/admin/activities" element={<ProtectedRoute allowedRoles={['super_admin']} data-id="element-25">
            <Navigation data-id="element-26" />
            <AdminActivities data-id="element-27" />
          </ProtectedRoute>} data-id="element-24" />
      <Route path="/admin/analytics" element={<ProtectedRoute allowedRoles={['super_admin']} data-id="element-29">
            <Navigation data-id="element-30" />
            <AdminAnalytics data-id="element-31" />
          </ProtectedRoute>} data-id="element-28" />
      <Route path="/admin/logs" element={<ProtectedRoute allowedRoles={['super_admin']} data-id="element-33">
            <Navigation data-id="element-34" />
            <AdminLogs data-id="element-35" />
          </ProtectedRoute>} data-id="element-32" />
      <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={['super_admin']} data-id="element-37">
            <Navigation data-id="element-38" />
            <AdminReports data-id="element-39" />
          </ProtectedRoute>} data-id="element-36" />
      <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['super_admin']} data-id="element-41">
            <Navigation data-id="element-42" />
            <AdminSettings data-id="element-43" />
          </ProtectedRoute>} data-id="element-40" />

      {/* Chapter Leader Routes */}
      <Route path="/chapter/dashboard" element={<ProtectedRoute allowedRoles={['ketua_chapter', 'ketua_student_chapter']} data-id="element-45">
            <Navigation data-id="element-46" />
            {user?.role === 'ketua_student_chapter' ? <StudentChapterDashboard data-id="element-47" /> : <ChapterDashboard data-id="element-48" />}
          </ProtectedRoute>} data-id="element-44" />
      <Route path="/chapter/members" element={<ProtectedRoute allowedRoles={['ketua_chapter', 'ketua_student_chapter']} data-id="element-50">
            <Navigation data-id="element-51" />
            <ChapterMembers data-id="element-52" />
          </ProtectedRoute>} data-id="element-49" />
      <Route path="/chapter/reports" element={<ProtectedRoute allowedRoles={['ketua_chapter', 'ketua_student_chapter']} data-id="element-54">
            <Navigation data-id="element-55" />
            <ChapterReports data-id="element-56" />
          </ProtectedRoute>} data-id="element-53" />

      {/* Komite Pusat Routes */}
      <Route path="/committee/dashboard" element={<ProtectedRoute allowedRoles={['komite_pusat']} data-id="element-58">
            <Navigation data-id="element-59" />
            <CommitteeDashboard data-id="element-60" />
          </ProtectedRoute>} data-id="element-57" />
      <Route path="/committee/policies" element={<ProtectedRoute allowedRoles={['komite_pusat']} data-id="element-62">
            <Navigation data-id="element-63" />
            <CommitteePolicies data-id="element-64" />
          </ProtectedRoute>} data-id="element-61" />
      <Route path="/committee/reports" element={<ProtectedRoute allowedRoles={['komite_pusat']} data-id="element-66">
            <Navigation data-id="element-67" />
            <CommitteeReports data-id="element-68" />
          </ProtectedRoute>} data-id="element-65" />
      <Route path="/committee/approvals" element={<ProtectedRoute allowedRoles={['komite_pusat']} data-id="element-70">
            <Navigation data-id="element-71" />
            <CommitteeApprovals data-id="element-72" />
          </ProtectedRoute>} data-id="element-69" />

      {/* Koordinator Acara Routes */}
      <Route path="/events/manage" element={<ProtectedRoute allowedRoles={['koordinator_acara', 'super_admin']} data-id="element-74">
            <Navigation data-id="element-75" />
            <EventCoordinatorDashboard data-id="element-76" />
          </ProtectedRoute>} data-id="element-73" />
      <Route path="/events/registrations" element={<ProtectedRoute allowedRoles={['koordinator_acara', 'super_admin']} data-id="element-78">
            <Navigation data-id="element-79" />
            <EventsRegistrations data-id="element-80" />
          </ProtectedRoute>} data-id="element-77" />
      <Route path="/events/certificates" element={<ProtectedRoute allowedRoles={['koordinator_acara', 'super_admin']} data-id="element-82">
            <Navigation data-id="element-83" />
            <EventsCertificates data-id="element-84" />
          </ProtectedRoute>} data-id="element-81" />
      <Route path="/events/reports" element={<ProtectedRoute allowedRoles={['koordinator_acara', 'super_admin']} data-id="element-86">
            <Navigation data-id="element-87" />
            <EventsReports data-id="element-88" />
          </ProtectedRoute>} data-id="element-85" />

      {/* Event Management Routes */}
      <Route path="/events/create" element={<ProtectedRoute allowedRoles={['ketua_chapter', 'ketua_student_chapter', 'anggota_organisasi', 'anggota_badan_usaha', 'mitra_asosiasi', 'koordinator_acara']} data-id="element-90">
            <Navigation data-id="element-91" />
            <CreateEventPage data-id="element-92" />
          </ProtectedRoute>} data-id="element-89" />
      <Route path="/committee/events/approvals" element={<ProtectedRoute allowedRoles={['komite_pusat']} data-id="element-94">
            <Navigation data-id="element-95" />
            <EventApprovalsPage data-id="element-96" />
          </ProtectedRoute>} data-id="element-93" />

      {/* Bendahara Routes */}
      <Route path="/finance/dashboard" element={<ProtectedRoute allowedRoles={['bendahara', 'super_admin']} data-id="element-98">
            <Navigation data-id="element-99" />
            <FinanceDashboard data-id="element-100" />
          </ProtectedRoute>} data-id="element-97" />
      <Route path="/finance/invoices" element={<ProtectedRoute allowedRoles={['bendahara', 'super_admin']} data-id="element-102">
            <Navigation data-id="element-103" />
            <FinanceInvoices data-id="element-104" />
          </ProtectedRoute>} data-id="element-101" />
      <Route path="/finance/reports" element={<ProtectedRoute allowedRoles={['bendahara', 'super_admin']} data-id="element-106">
            <Navigation data-id="element-107" />
            <FinanceReports data-id="element-108" />
          </ProtectedRoute>} data-id="element-105" />
      <Route path="/finance/export" element={<ProtectedRoute allowedRoles={['bendahara', 'super_admin']} data-id="element-110">
            <Navigation data-id="element-111" />
            <FinanceReports data-id="element-112" />
          </ProtectedRoute>} data-id="element-109" />

      {/* Company/Organization Management Routes */}
      <Route path="/company/jobs" element={<ProtectedRoute allowedRoles={['anggota_badan_usaha']} data-id="element-114">
            <Navigation data-id="element-115" />
            <JobManagementPage data-id="element-116" />
          </ProtectedRoute>} data-id="element-113" />
      <Route path="/organization/members" element={<ProtectedRoute allowedRoles={['anggota_organisasi']} data-id="element-118">
            <Navigation data-id="element-119" />
            <MemberManagementPage data-id="element-120" />
          </ProtectedRoute>} data-id="element-117" />
      <Route path="/jobs/create" element={<ProtectedRoute allowedRoles={['anggota_badan_usaha']} data-id="element-122">
            <Navigation data-id="element-123" />
            <CreateJobPage data-id="element-124" />
          </ProtectedRoute>} data-id="element-121" />

      {/* Member Routes */}
      <Route path="/jobs/:id/apply" element={<ProtectedRoute data-id="element-126">
            <Navigation data-id="element-127" />
            <JobApplicationPage data-id="element-128" />
          </ProtectedRoute>} data-id="element-125" />
      <Route path="/events/:id/register" element={<ProtectedRoute data-id="element-130">
            <Navigation data-id="element-131" />
            <EventRegistrationPage data-id="element-132" />
          </ProtectedRoute>} data-id="element-129" />
      <Route path="/payment/:id" element={<ProtectedRoute data-id="element-134">
            <Navigation data-id="element-135" />
            <PaymentGateway data-id="element-136" />
          </ProtectedRoute>} data-id="element-133" />
      <Route path="/dashboard" element={<ProtectedRoute data-id="element-138">
            <Navigation data-id="element-139" />
            <Dashboard data-id="element-140" />
          </ProtectedRoute>} data-id="element-137" />
      <Route path="/profile" element={<ProtectedRoute data-id="element-142">
            <Navigation data-id="element-143" />
            <ProfilePage data-id="element-144" />
          </ProtectedRoute>} data-id="element-141" />
      <Route path="/profile/edit" element={<ProtectedRoute data-id="element-146">
            <Navigation data-id="element-147" />
            <EditProfilePage data-id="element-148" />
          </ProtectedRoute>} data-id="element-145" />
      <Route path="/certificates" element={<ProtectedRoute data-id="element-150">
            <Navigation data-id="element-151" />
            <CertificateDownloadPage data-id="element-152" />
          </ProtectedRoute>} data-id="element-149" />
      <Route path="/settings" element={<ProtectedRoute data-id="element-154">
            <Navigation data-id="element-155" />
            <SettingsPage data-id="element-156" />
          </ProtectedRoute>} data-id="element-153" />
      <Route path="/payment" element={<ProtectedRoute data-id="element-158">
            <Navigation data-id="element-159" />
            <PaymentPage data-id="element-160" />
          </ProtectedRoute>} data-id="element-157" />
      <Route path="/jobs" element={<ProtectedRoute data-id="element-162">
            <Navigation data-id="element-163" />
            <JobListings data-id="element-164" />
          </ProtectedRoute>} data-id="element-161" />
      <Route path="/jobs/:id" element={<ProtectedRoute data-id="element-166">
            <Navigation data-id="element-167" />
            <JobDetail data-id="element-168" />
          </ProtectedRoute>} data-id="element-165" />
      <Route path="/benefits" element={<ProtectedRoute data-id="element-170">
            <Navigation data-id="element-171" />
            <BenefitsPage data-id="element-172" />
          </ProtectedRoute>} data-id="element-169" />
      <Route path="/benefits/history" element={<ProtectedRoute data-id="element-174">
            <Navigation data-id="element-175" />
            <BenefitHistoryPage data-id="element-176" />
          </ProtectedRoute>} data-id="element-173" />
      <Route path="/benefits/:id" element={<ProtectedRoute data-id="element-178">
            <Navigation data-id="element-179" />
            <BenefitDetailPage data-id="element-180" />
          </ProtectedRoute>} data-id="element-177" />
      <Route path="/benefits/:id/claim" element={<ProtectedRoute data-id="element-182">
            <Navigation data-id="element-183" />
            <BenefitClaimPage data-id="element-184" />
          </ProtectedRoute>} data-id="element-181" />
      <Route path="/chapter" element={<ProtectedRoute data-id="element-186">
            <Navigation data-id="element-187" />
            <ChapterPage data-id="element-188" />
          </ProtectedRoute>} data-id="element-185" />
      <Route path="/chapter/:id" element={<ProtectedRoute data-id="element-190">
            <Navigation data-id="element-191" />
            <ChapterDetailPage data-id="element-192" />
          </ProtectedRoute>} data-id="element-189" />
      <Route path="/events" element={<ProtectedRoute data-id="element-194">
            <Navigation data-id="element-195" />
            <EventsPage data-id="element-196" />
          </ProtectedRoute>} data-id="element-193" />
      <Route path="/events/:id" element={<ProtectedRoute data-id="element-198">
            <Navigation data-id="element-199" />
            <EventDetail data-id="element-200" />
          </ProtectedRoute>} data-id="element-197" />
      <Route path="/meetings/:id" element={<ProtectedRoute data-id="element-202">
            <Navigation data-id="element-203" />
            <MeetingDetail data-id="element-204" />
          </ProtectedRoute>} data-id="element-201" />
      <Route path="/announcements" element={<ProtectedRoute data-id="element-206">
            <Navigation data-id="element-207" />
            <AnnouncementsPage data-id="element-208" />
          </ProtectedRoute>} data-id="element-205" />
      <Route path="/knowledge-center" element={<ProtectedRoute data-id="element-210">
            <Navigation data-id="element-211" />
            <KnowledgeCenter data-id="element-212" />
          </ProtectedRoute>} data-id="element-209" />
      <Route path="/membership/upgrade" element={<ProtectedRoute data-id="element-214">
            <Navigation data-id="element-215" />
            <UpgradeMembershipPage data-id="element-216" />
          </ProtectedRoute>} data-id="element-213" />
      <Route path="/documents/:id" element={<ProtectedRoute data-id="element-218">
            <Navigation data-id="element-219" />
            <DocumentViewerPage data-id="element-220" />
          </ProtectedRoute>} data-id="element-217" />

      {/* Error Pages */}
      <Route path="/unauthorized" element={<UnauthorizedPage data-id="element-222" />} data-id="element-221" />
      <Route path="*" element={<NotFoundPage data-id="element-224" />} data-id="element-223" />

      {/* Default Route */}
      <Route path="/" element={isAuthenticated ? <Navigate to={getDefaultRoute(user?.role)} replace data-id="element-226" /> : <Navigate to="/login" replace data-id="element-227" />} data-id="element-225" />

      {/* Catch all - Redirect ke home */}
      <Route path="*" element={<Navigate to="/" replace data-id="element-229" />} data-id="element-228" />
    </Routes>;
}
function getDefaultRoute(role?: string) {
  switch (role) {
    case 'super_admin':
      return '/admin/dashboard';
    case 'ketua_chapter':
    case 'ketua_student_chapter':
      return '/chapter/dashboard';
    case 'komite_pusat':
      return '/committee/dashboard';
    case 'koordinator_acara':
      return '/events/manage';
    case 'bendahara':
      return '/finance/dashboard';
    default:
      return '/dashboard';
  }
}
export default function App() {
  return <ErrorBoundary data-id="element-230">
      <AuthProvider data-id="element-231">
        <BrowserRouter data-id="element-232">
          <AppRoutes data-id="element-233" />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>;
}